import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from './App';
import { useTheme } from './App';
import { 
  UserIcon, 
  ShieldCheckIcon, 
  EyeIcon, 
  DocumentTextIcon,
  CalendarDaysIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import api from './api';
import AddCourseModal from './components/AddCourseModal';

const API = "https://dvtime-1c6x.onrender.com";

const ACCENT_COLORS = [
  { 
    name: 'Indigo', 
    value: 'indigo', 
    bg: 'bg-indigo-500',
    colors: {
      50: '#eef2ff', 100: '#e0e7ff', 200: '#c7d2fe', 300: '#a5b4fc',
      400: '#818cf8', 500: '#6366f1', 600: '#4f46e5', 700: '#4338ca',
      800: '#3730a3', 900: '#312e81'
    }
  },
  { 
    name: 'Blue', 
    value: 'blue', 
    bg: 'bg-blue-500',
    colors: {
      50: '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 300: '#93c5fd',
      400: '#60a5fa', 500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8',
      800: '#1e40af', 900: '#1e3a8a'
    }
  },
  { 
    name: 'Purple', 
    value: 'purple', 
    bg: 'bg-purple-500',
    colors: {
      50: '#faf5ff', 100: '#f3e8ff', 200: '#e9d5ff', 300: '#d8b4fe',
      400: '#c084fc', 500: '#a855f7', 600: '#9333ea', 700: '#7e22ce',
      800: '#6b21a8', 900: '#581c87'
    }
  },
  { 
    name: 'Teal', 
    value: 'teal', 
    bg: 'bg-teal-500',
    colors: {
      50: '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 300: '#5eead4',
      400: '#2dd4bf', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e',
      800: '#115e59', 900: '#134e4a'
    }
  },
  { 
    name: 'Amber', 
    value: 'amber', 
    bg: 'bg-amber-500',
    colors: {
      50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d',
      400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309',
      800: '#92400e', 900: '#78350f'
    }
  },
  { 
    name: 'Rose', 
    value: 'rose', 
    bg: 'bg-rose-500',
    colors: {
      50: '#fff1f2', 100: '#ffe4e6', 200: '#fecdd3', 300: '#fda4af',
      400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c',
      800: '#9f1239', 900: '#881337'
    }
  },
  { 
    name: 'Emerald', 
    value: 'emerald', 
    bg: 'bg-emerald-500',
    colors: {
      50: '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7',
      400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#047857',
      800: '#065f46', 900: '#064e3b'
    }
  },
];

function ConfirmDeleteModal({ isOpen, onClose, onConfirm }) {
  const [confirmText, setConfirmText] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-md w-full mx-4 p-6 border border-gray-200 dark:border-gray-800"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Delete Account</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          This action cannot be undone. All your data will be permanently deleted.
        </p>
        
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Type <span className="font-bold text-red-600">DELETE</span> to confirm:
        </p>
        
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-xl text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-red-500 mb-4"
          placeholder="Type DELETE"
        />
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (confirmText === 'DELETE') {
                onConfirm();
              }
            }}
            disabled={confirmText !== 'DELETE'}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Delete Account
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Settings({ onClose, initialSection }) {
  const { user, logout } = useAuth();
  const [theme, setTheme] = useTheme();
  const [activeSection, setActiveSection] = useState(initialSection || 'account');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success', 'error', or null
  // Prefer backend-stored profile picture first, fall back to localStorage for legacy support
  const [profileImage, setProfileImage] = useState(user?.profile_picture || localStorage.getItem('profileImage') || null);
  const fileInputRef = useRef(null);
  const [transcripts, setTranscripts] = useState([]);
  const [editingTranscriptId, setEditingTranscriptId] = useState(null);
  const [editingTranscriptName, setEditingTranscriptName] = useState('');
  const [showUploadPage, setShowUploadPage] = useState(false);
  const [loadingTranscripts, setLoadingTranscripts] = useState(true);
  const [transcriptStatus, setTranscriptStatus] = useState(null); // 'uploading', 'success', 'error'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [activeUploadId, setActiveUploadId] = useState(null);
  const progressIntervalRef = useRef(null);
  const progressPollIntervalRef = useRef(null);
  
  // Custom courses state
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [customCourses, setCustomCourses] = useState([]);
  const [schools, setSchools] = useState(['DVHS']); // List of schools from transcript
  const [manualSchools, setManualSchools] = useState([]); // List of manually added schools
  
  // Add school modal state
  const [showAddSchoolModal, setShowAddSchoolModal] = useState(false);
  const [newSchool, setNewSchool] = useState({ name: '', type: 'online-hs' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteTranscriptConfirm, setDeleteTranscriptConfirm] = useState(null);
  
  // Student info from transcript
  const [studentInfo, setStudentInfo] = useState({
    full_name: '',
    student_id: '',
    grade_level: ''
  });
  
  // Settings state
  const [settings, setSettings] = useState({
    preferredName: localStorage.getItem('preferredName') || user?.preferred_name || '',
    pronouns: localStorage.getItem('pronouns') || user?.pronouns || '',
    email: user?.email || '',
    accentColor: localStorage.getItem('accentColor') || 'indigo',
    fontScale: 100,
    reducedMotion: false,
    highContrast: false,
    gpaVisibility: JSON.parse(localStorage.getItem('gpaVisibility')) || {
      weighted: true,
      unweighted: true,
      tenTwelve: true,
      calGrant: true
    }
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Load user settings from localStorage or user object
    if (user) {
      setSettings(prev => ({
        ...prev,
        preferredName: localStorage.getItem('preferredName') || user.preferred_name || '',
        pronouns: localStorage.getItem('pronouns') || user.pronouns || '',
        email: user.email || '',
        // Keep a copy of the backend profile picture in settings so we can send it back
        profilePicture: user.profile_picture || prev.profilePicture || null,
      }));

      // Sync profile image state from backend if available
      if (user.profile_picture) {
        setProfileImage(user.profile_picture);
      }
    }
    
    // Fetch transcript data to get full name, student ID, and grade level
    fetchTranscriptData();
    
    // Fetch all transcripts
    fetchTranscripts();
    
    // Apply saved accent color on mount
    applyAccentColor(settings.accentColor);
  }, [user]);

  const fetchTranscripts = useCallback(async () => {
    try {
      setLoadingTranscripts(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/transcript/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Map API response to frontend format
      const mappedTranscripts = response.data.transcripts.map(t => ({
        id: t.id,
        name: t.name,
        uploadDate: t.upload_date,
        isActive: t.is_active,
        isParsing: t.parse_status === 'parsing' || t.parse_status === 'pending',
        parseProgress: t.parse_progress || 0,
        parseStatus: t.parse_status,
        errorMessage: t.error_message,
        hasPreviousVersion: !!t.previous_parsed_data
      }));
      
      setTranscripts(mappedTranscripts);
    } catch (error) {
      console.error('Error fetching transcripts:', error);
    } finally {
      setLoadingTranscripts(false);
    }
  }, []);

  const applyAccentColor = (colorName) => {
    const colorConfig = ACCENT_COLORS.find(c => c.value === colorName);
    if (!colorConfig) return;
    
    const root = document.documentElement;
    Object.entries(colorConfig.colors).forEach(([shade, hex]) => {
      root.style.setProperty(`--accent-${shade}`, hex);
    });
    
    // Store in localStorage
    localStorage.setItem('accentColor', colorName);
  };

  const fetchTranscriptData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/transcript/data`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && response.data.student_info) {
        const info = response.data.student_info;
        setStudentInfo({
          full_name: info.name || info.full_name || '',
          student_id: info.student_id || 'Not Available',
          grade_level: info.grade || info.grade_level || ''
        });
      }
    } catch (error) {
      console.log('Could not fetch transcript data:', error);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(null), 2000);
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(null), 2000);
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        // Store in both state and settings so it can be sent to backend
        setProfileImage(reader.result);
        localStorage.setItem('profileImage', reader.result);
        setSettings(prev => ({
          ...prev,
          profilePicture: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveSettings = async () => {
    try {
      // Save to localStorage for immediate access
      localStorage.setItem('accentColor', settings.accentColor);
      localStorage.setItem('preferredName', settings.preferredName);
      localStorage.setItem('pronouns', settings.pronouns);
      localStorage.setItem('gpaVisibility', JSON.stringify(settings.gpaVisibility));
      
      // Save profile data to backend (preferred name, pronouns, profile picture)
      try {
        await api.post('/user/profile', {
          preferred_name: settings.preferredName,
          pronouns: settings.pronouns,
          profile_picture: settings.profilePicture || null
        });
        console.log('Profile saved to database successfully');
      } catch (apiError) {
        console.error('Error saving profile to backend:', apiError);
        // Still show success since localStorage worked
      }
      
      // Show success state
      setSaveStatus('success');
      setTimeout(() => {
        setSaveStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2000);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match!');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2000);
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      alert('New password must be at least 6 characters long!');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2000);
      return;
    }
    
    try {
      await api.post('/user/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 2000);
      alert('Password changed successfully!');
    } catch (error) {
      console.error('Error changing password:', error);
      alert(error.response?.data?.detail || 'Failed to change password. Please check your current password.');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2000);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete(`${API}/user/account`);
      logout();
    } catch (error) {
      console.error('Error deleting account:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2000);
    }
  };

  // Custom courses functions
  const fetchCustomCourses = async () => {
    try {
      const response = await api.get('/user/custom-courses');
      setCustomCourses(response.data.custom_courses || []);
    } catch (error) {
      console.error('Error fetching custom courses:', error);
    }
  };

  const fetchSchools = async () => {
    try {
      const response = await api.get('/user/schools');
      setSchools(response.data.schools || ['DVHS']);
      setManualSchools(response.data.manual_schools || []);
    } catch (error) {
      console.error('Error fetching schools:', error);
      setSchools(['DVHS']);
      setManualSchools([]);
    }
  };

  const handleAddSchool = async () => {
    if (!newSchool.name.trim()) {
      setNewSchool({ ...newSchool, error: 'Please enter a school name' });
      return;
    }
    
    setNewSchool({ ...newSchool, loading: true, error: null });
    
    try {
      await api.post('/user/schools', {
        school_name: newSchool.name.trim(),
        school_type: newSchool.type
      });
      
      await fetchSchools();
      setShowAddSchoolModal(false);
      setNewSchool({ name: '', type: 'online-hs', loading: false, error: null, success: true });
    } catch (error) {
      console.error('Error adding school:', error);
      setNewSchool({ ...newSchool, loading: false, error: 'Failed to add school. Please try again.' });
    }
  };

  const handleDeleteSchool = async (schoolName) => {
    // Only allow deleting manually added schools
    if (!manualSchools.includes(schoolName)) {
      return;
    }
    
    try {
      // Use POST-style body for DELETE request
      await api.request({
        method: 'DELETE',
        url: '/user/schools',
        data: { school_name: schoolName }
      });
      
      // Refresh the schools list
      await fetchSchools();
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting school:', error);
      console.error('Error response:', error.response?.data);
      setDeleteConfirm(null);
    }
  };

  const handleAddCustomCourse = async (courseData) => {
    try {
      await api.post('/user/custom-courses', {
        course_name: courseData.courseName,
        grade: courseData.grade,
        credits: courseData.credits,
        course_level: courseData.courseLevel,
        grade_level: `Grade ${courseData.gradeLevel.padStart(2, '0')}`, // Format as "Grade 09", "Grade 10", etc.
        term: `Term ${courseData.term}`, // Format as "Term 1", "Term 2", etc.
        institution: courseData.institution
      });
      
      setShowAddCourseModal(false);
      await fetchCustomCourses();
      alert('Course added successfully!');
    } catch (error) {
      console.error('Error adding custom course:', error);
      alert('Failed to add course. Please try again.');
    }
  };

  const handleDeleteCustomCourse = async (courseId) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        await api.delete(`/user/custom-courses/${courseId}`);
        await fetchCustomCourses();
        alert('Course deleted successfully!');
      } catch (error) {
        console.error('Error deleting custom course:', error);
        alert('Failed to delete course. Please try again.');
      }
    }
  };

  const calculateGradYear = () => {
    const gradeLevel = studentInfo.grade_level || user?.grade_level;
    if (!gradeLevel) return 'N/A';
    const gradeNum = parseInt(gradeLevel);
    if (isNaN(gradeNum)) return 'N/A';
    
    // Current year is 2025
    // 12th grade graduates in 2026 (2025 + 1)
    // 11th grade graduates in 2027 (2025 + 2)
    // Formula: 2025 + (13 - grade)
    const currentYear = 2025;
    return currentYear + (13 - gradeNum);
  };

  const getInitials = () => {
    const name = settings.preferredName || user?.full_name || 'User';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getDisplayName = () => {
    return settings.preferredName || user?.full_name?.split(' ')[0] || 'User';
  };

  const handleRenameTranscript = async (id, newName) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/transcript/${id}/rename`, 
        { name: newName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTranscripts(transcripts.map(t => 
        t.id === id ? { ...t, name: newName } : t
      ));
      setEditingTranscriptId(null);
      setEditingTranscriptName('');
    } catch (error) {
      console.error('Error renaming transcript:', error);
      setTranscriptStatus('error');
      setTimeout(() => setTranscriptStatus(null), 2000);
    }
  };

  const handleSetActiveTranscript = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/transcript/${id}/activate`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTranscripts(transcripts.map(t => ({
        ...t,
        isActive: t.id === id
      })));
      
      // Dispatch event to notify Dashboard to refetch transcript
      window.dispatchEvent(new CustomEvent('transcriptChanged'));
      
      // Optionally refetch transcript data without full page reload
      fetchTranscriptData();
    } catch (error) {
      console.error('Error activating transcript:', error);
      setTranscriptStatus('error');
      setTimeout(() => setTranscriptStatus(null), 2000);
    }
  };

  const handleDeleteTranscript = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/transcript/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updatedTranscripts = transcripts.filter(t => t.id !== id);
      setTranscripts(updatedTranscripts);
      
      // Close the confirmation popup
      setDeleteTranscriptConfirm(null);
      
      // If no transcripts left, refresh transcript data to show empty state
      if (updatedTranscripts.length === 0) {
        await fetchTranscriptData();
      }
    } catch (error) {
      console.error('Error deleting transcript:', error);
      alert('Failed to delete transcript. Please try again.');
      setDeleteTranscriptConfirm(null);
    }
  };


  const handleUploadNewTranscript = async (file) => {
    if (!file) {
      setTranscriptStatus('error');
      setTimeout(() => setTranscriptStatus(null), 3000);
      return;
    }

    try {
      setTranscriptStatus('uploading');
      setUploadProgress(0);
      setUploadStage('Uploading file...');
      
      const formData = new FormData();
      formData.append('file', file);
      
      // Start the upload
      const response = await api.post('/transcript/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          // Show upload progress (0-100% of file upload)
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Map upload progress to 0-15% of total progress
          const mappedProgress = Math.round(percentCompleted * 0.15);
          setUploadProgress(mappedProgress);
          setUploadStage('Uploading file...');
        }
      });
      
      const transcriptId = response.data.id;
      setActiveUploadId(transcriptId);
      
      setUploadStage('Processing transcript...');
      
      // Start polling for real-time parsing progress
      progressPollIntervalRef.current = setInterval(async () => {
        try {
          const statusResponse = await api.get(`/transcript/${transcriptId}/status`);
          const { parse_status, parse_progress } = statusResponse.data;
          
          setUploadProgress(parse_progress);
          
          // Update stage based on progress
          if (parse_progress < 10) {
            setUploadStage('Starting parser...');
          } else if (parse_progress < 15) {
            setUploadStage('Loading PDF file...');
          } else if (parse_progress < 25) {
            setUploadStage('Reading document...');
          } else if (parse_progress < 35) {
            setUploadStage('Converting PDF to images...');
          } else if (parse_progress < 60) {
            setUploadStage('Processing with AI vision...');
          } else if (parse_progress < 70) {
            setUploadStage('AI processing complete...');
          } else if (parse_progress < 80) {
            setUploadStage('Validating data...');
          } else if (parse_progress < 95) {
            setUploadStage('Extracting course data...');
          } else if (parse_progress < 100) {
            setUploadStage('Finalizing...');
          } else {
            setUploadStage('Complete!');
          }
          
          // Check if completed or failed
          if (parse_status === 'completed') {
            clearInterval(progressPollIntervalRef.current);
            progressPollIntervalRef.current = null;
            setActiveUploadId(null);
            
            setUploadProgress(100);
            setUploadStage('Complete!');
            
            // Refresh transcript list
            await fetchTranscripts();
            
            setTranscriptStatus('success');
            setTimeout(() => {
              setTranscriptStatus(null);
              setUploadProgress(0);
              setUploadStage('');
            }, 2000);
          } else if (parse_status === 'failed') {
            clearInterval(progressPollIntervalRef.current);
            progressPollIntervalRef.current = null;
            setActiveUploadId(null);
            
            setTranscriptStatus('error');
            setUploadProgress(0);
            setUploadStage('');
            alert('Transcript parsing failed. Please try again.');
            setTimeout(() => setTranscriptStatus(null), 3000);
          }
        } catch (pollError) {
          console.error('Error polling transcript status:', pollError);
        }
      }, 500); // Poll every 500ms for smooth progress updates
      
    } catch (error) {
      console.error('Error uploading transcript:', error);
      
      const errorMsg = error.response?.data?.detail || error.message || 'Upload failed';
      alert(`Upload Error: ${errorMsg}`);
      
      setTranscriptStatus('error');
      setUploadProgress(0);
      setUploadStage('');
      setActiveUploadId(null);
      
      setTimeout(() => setTranscriptStatus(null), 3000);
    }
  };


  // Check for ongoing uploads on mount and restore progress
  useEffect(() => {
    const checkOngoingUploads = async () => {
      try {
        const response = await api.get('/transcript/list');
        const parsingTranscript = response.data.transcripts.find(
          t => t.parse_status === 'parsing' || t.parse_status === 'pending'
        );
        
        if (parsingTranscript) {
          setActiveUploadId(parsingTranscript.id);
          setTranscriptStatus('uploading');
          setUploadProgress(parsingTranscript.parse_progress || 0);
          
          // Start polling for this transcript
          progressPollIntervalRef.current = setInterval(async () => {
            try {
              const statusResponse = await api.get(`/transcript/${parsingTranscript.id}/status`);
              const { parse_status, parse_progress } = statusResponse.data;
              
              setUploadProgress(parse_progress);
              
              if (parse_progress < 10) setUploadStage('Starting parser...');
              else if (parse_progress < 15) setUploadStage('Loading PDF file...');
              else if (parse_progress < 25) setUploadStage('Reading document...');
              else if (parse_progress < 35) setUploadStage('Converting PDF to images...');
              else if (parse_progress < 60) setUploadStage('Processing with AI vision...');
              else if (parse_progress < 70) setUploadStage('AI processing complete...');
              else if (parse_progress < 80) setUploadStage('Validating data...');
              else if (parse_progress < 95) setUploadStage('Extracting course data...');
              else if (parse_progress < 100) setUploadStage('Finalizing...');
              else setUploadStage('Complete!');
              
              if (parse_status === 'completed' || parse_status === 'failed') {
                clearInterval(progressPollIntervalRef.current);
                progressPollIntervalRef.current = null;
                setActiveUploadId(null);
                setTranscriptStatus(parse_status === 'completed' ? 'success' : 'error');
                await fetchTranscripts();
                setTimeout(() => {
                  setTranscriptStatus(null);
                  setUploadProgress(0);
                  setUploadStage('');
                }, 3000);
              }
            } catch (error) {
              console.error('Error polling status:', error);
            }
          }, 1000);
        }
      } catch (error) {
        console.error('Error checking ongoing uploads:', error);
      }
    };
    
    checkOngoingUploads();
    
    return () => {
      if (progressPollIntervalRef.current) {
        clearInterval(progressPollIntervalRef.current);
      }
    };
  }, []); // Run once on mount

  // Poll for transcript parsing status
  useEffect(() => {
    const hasParsingTranscripts = transcripts.some(t => t.isParsing);
    
    if (hasParsingTranscripts) {
      const pollInterval = setInterval(() => {
        fetchTranscripts();
      }, 2000); // Poll every 2 seconds
      
      return () => clearInterval(pollInterval);
    }
  }, [transcripts, fetchTranscripts]);

  // Set active section when initialSection prop changes
  useEffect(() => {
    if (initialSection) {
      setActiveSection(initialSection);
    }
  }, [initialSection]);

  // Fetch custom courses and schools on mount
  useEffect(() => {
    fetchCustomCourses();
    fetchSchools();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  const sections = [
    { id: 'account', name: 'Account', icon: UserIcon },
    { id: 'security', name: 'Security & Privacy', icon: ShieldCheckIcon },
    { id: 'transcript', name: 'Transcript', icon: DocumentTextIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Profile & Settings</h1>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="sticky top-24 flex flex-col gap-2">
              {sections.map(section => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                      activeSection === section.id
                        ? 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 ring-1 ring-indigo-500/40'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{section.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 space-y-6">
            {/* Account Settings */}
            {activeSection === 'account' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-900/60 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-gray-800">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account Information</h2>
                  
                  {/* Avatar */}
                  <div className="flex items-center gap-6 mb-6">
                    <div className="relative">
                      {profileImage ? (
                        <img
                          src={profileImage}
                          alt="Profile"
                          className="h-24 w-24 rounded-full object-cover ring-2 ring-gray-300 dark:ring-gray-700"
                        />
                      ) : (
                        <div className="h-24 w-24 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 text-white font-bold text-3xl flex items-center justify-center ring-2 ring-gray-300 dark:ring-gray-700">
                          {getInitials()}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">{getDisplayName()}</p>
                      {settings.pronouns && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{settings.pronouns}</p>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                      >
                        Upload new picture
                      </button>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Max 5MB • JPG, PNG, GIF, WebP</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={studentInfo.full_name || user?.full_name || user?.name || ''}
                        disabled
                        className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-xl text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">From your signup information.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Preferred Name
                      </label>
                      <input
                        type="text"
                        value={settings.preferredName}
                        onChange={(e) => setSettings({...settings, preferredName: e.target.value})}
                        placeholder="How should we call you?"
                        className="w-full px-4 py-2 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-xl text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Pronouns
                      </label>
                      <input
                        type="text"
                        value={settings.pronouns}
                        onChange={(e) => setSettings({...settings, pronouns: e.target.value})}
                        placeholder="e.g., he/him, she/her, they/them"
                        className="w-full px-4 py-2 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-xl text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={settings.email}
                        onChange={(e) => setSettings({...settings, email: e.target.value})}
                        className="w-full px-4 py-2 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-xl text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                  </div>

                  <button
                    onClick={handleSaveSettings}
                    disabled={saveStatus !== null}
                    className={`mt-6 w-full px-4 py-2 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 ${
                      saveStatus === 'success'
                        ? 'bg-green-600 text-white'
                        : saveStatus === 'error'
                        ? 'bg-red-600 text-white'
                        : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600'
                    }`}
                  >
                    {saveStatus === 'success' ? (
                      <>
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Saved!
                      </>
                    ) : saveStatus === 'error' ? (
                      <>
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Failed
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>

                {/* Theme & Accent */}
                <div className="bg-white dark:bg-gray-900/60 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-gray-800">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Appearance</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Theme
                      </label>
                      <div className="flex gap-3">
                        {['light', 'dark'].map(themeOption => (
                          <button
                            key={themeOption}
                            onClick={() => setTheme(themeOption)}
                            className={`flex-1 px-4 py-2 rounded-xl font-medium capitalize transition-all ${
                              theme === themeOption
                                ? 'bg-indigo-500 text-white'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            {themeOption}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Accent Color
                      </label>
                      <div className="grid grid-cols-4 gap-3">
                        {ACCENT_COLORS.map(color => (
                          <button
                            key={color.value}
                            onClick={() => {
                              setSettings({...settings, accentColor: color.value});
                              applyAccentColor(color.value);
                            }}
                            className={`flex flex-col items-center gap-2 p-4 rounded-xl border-3 transition-all ${
                              settings.accentColor === color.value
                                ? 'border-gray-900 dark:border-white bg-gray-100 dark:bg-gray-800 shadow-lg scale-105'
                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md'
                            }`}
                            style={{
                              borderWidth: settings.accentColor === color.value ? '3px' : '2px'
                            }}
                          >
                            <div 
                              className="w-10 h-10 rounded-full shadow-md ring-2 ring-offset-2 ring-gray-300 dark:ring-gray-600"
                              style={{ backgroundColor: color.colors[500] }}
                            />
                            <span className={`text-sm font-semibold ${
                              settings.accentColor === color.value 
                                ? 'text-gray-900 dark:text-white' 
                                : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {color.name}
                            </span>
                            {settings.accentColor === color.value && (
                              <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security & Privacy Settings */}
            {activeSection === 'security' && (
              <div className="space-y-6">
                {/* Change Password */}
                <div className="bg-white dark:bg-gray-900/60 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-gray-800">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Change Password</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Current Password
                      </label>
                      <input
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                        className="w-full px-4 py-2 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-xl text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        New Password
                      </label>
                      <input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        className="w-full px-4 py-2 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-xl text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        className="w-full px-4 py-2 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-xl text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <button
                      onClick={handleChangePassword}
                      disabled={saveStatus !== null}
                      className={`w-full px-4 py-2 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 ${
                        saveStatus === 'success'
                          ? 'bg-green-600 text-white'
                          : saveStatus === 'error'
                          ? 'bg-red-600 text-white'
                          : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600'
                      }`}
                    >
                      {saveStatus === 'success' ? (
                        <>
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Updated!
                        </>
                      ) : saveStatus === 'error' ? (
                        <>
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Failed
                        </>
                      ) : (
                        'Update Password'
                      )}
                    </button>
                  </div>
                </div>

                {/* Account Deletion */}
                <div className="bg-white dark:bg-gray-900/60 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-gray-800">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account Deletion</h2>
                  
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Once you delete your account, there is no going back. Please be certain.
                  </p>

                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            )}

            {/* Transcript Settings */}
            {activeSection === 'transcript' && (
              <div className="space-y-6">
                {/* GPA Visibility */}
                <div className="bg-white dark:bg-gray-900/60 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-gray-800">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">GPA Visibility</h2>
                  
                  <div className="space-y-3">
                    <label className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">Weighted GPA</span>
                      <input
                        type="checkbox"
                        checked={settings.gpaVisibility.weighted}
                        onChange={(e) => setSettings({
                          ...settings,
                          gpaVisibility: {...settings.gpaVisibility, weighted: e.target.checked}
                        })}
                        className="w-5 h-5 rounded focus:ring-2 accent-checkbox"
                        style={{ accentColor: `var(--accent-600)` }}
                      />
                    </label>
                    <label className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">Unweighted GPA</span>
                      <input
                        type="checkbox"
                        checked={settings.gpaVisibility.unweighted}
                        onChange={(e) => setSettings({
                          ...settings,
                          gpaVisibility: {...settings.gpaVisibility, unweighted: e.target.checked}
                        })}
                        className="w-5 h-5 rounded focus:ring-2 accent-checkbox"
                        style={{ accentColor: `var(--accent-600)` }}
                      />
                    </label>
                    <label className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">10-12 Weighted</span>
                      <input
                        type="checkbox"
                        checked={settings.gpaVisibility.tenTwelve}
                        onChange={(e) => setSettings({
                          ...settings,
                          gpaVisibility: {...settings.gpaVisibility, tenTwelve: e.target.checked}
                        })}
                        className="w-5 h-5 rounded focus:ring-2 accent-checkbox"
                        style={{ accentColor: `var(--accent-600)` }}
                      />
                    </label>
                    <label className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">CA Cal Grant</span>
                      <input
                        type="checkbox"
                        checked={settings.gpaVisibility.calGrant}
                        onChange={(e) => setSettings({
                          ...settings,
                          gpaVisibility: {...settings.gpaVisibility, calGrant: e.target.checked}
                        })}
                        className="w-5 h-5 rounded focus:ring-2 accent-checkbox"
                        style={{ accentColor: `var(--accent-600)` }}
                      />
                    </label>
                  </div>

                  <button
                    onClick={handleSaveSettings}
                    disabled={saveStatus !== null}
                    className={`mt-6 w-full px-4 py-2 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 ${
                      saveStatus === 'success'
                        ? 'bg-green-600 text-white'
                        : saveStatus === 'error'
                        ? 'bg-red-600 text-white'
                        : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600'
                    }`}
                  >
                    {saveStatus === 'success' ? (
                      <>
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Saved!
                      </>
                    ) : saveStatus === 'error' ? (
                      <>
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Failed
                      </>
                    ) : (
                      'Save Preferences'
                    )}
                  </button>
                </div>

                {/* School Management */}
                <div className="bg-white dark:bg-gray-900/60 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-gray-800">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Schools Attended</h2>
                  
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                    Add schools you've attended for outside courses (e.g., community colleges, online high schools).
                  </p>

                  {/* School List */}
                  <div className="mb-4">
                    <div className="space-y-2">
                      {schools.map((school, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg group">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 flex-1 min-w-0">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 break-words">{school}</span>
                            {!manualSchools.includes(school) && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 italic whitespace-nowrap">(from transcript)</span>
                            )}
                          </div>
                          {manualSchools.includes(school) && (
                            <div className="relative flex items-center gap-2 flex-shrink-0 ml-2">
                              <button
                                onClick={() => setDeleteConfirm(school)}
                                className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1"
                                title="Delete manually added school"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                              {deleteConfirm === school && (
                                <div className="absolute right-0 top-full mt-1 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 flex gap-2">
                                  <button
                                    onClick={() => handleDeleteSchool(school)}
                                    className="px-3 py-1 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                                  >
                                    Delete
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirm(null)}
                                    className="px-3 py-1 text-xs font-semibold bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Add School Button */}
                  <button
                    onClick={() => setShowAddSchoolModal(true)}
                    className="w-full px-4 py-3 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 text-white"
                    style={{
                      background: 'linear-gradient(to right, var(--accent-500), var(--accent-600))'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(to right, var(--accent-600), var(--accent-700))';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(to right, var(--accent-500), var(--accent-600))';
                    }}
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add School
                  </button>
                </div>

                {/* Missing Courses */}
                <div data-section="custom-courses" className="bg-white dark:bg-gray-900/60 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-gray-800">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Missing Courses</h2>
                  
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                    Add courses that weren't parsed correctly from your transcript. These courses will be included in your total course count and GPA calculations according to the course level you select.
                  </p>
                  
                  <button
                    onClick={() => setShowAddCourseModal(true)}
                    className="w-full px-4 py-3 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 text-white mb-4"
                    style={{
                      background: 'linear-gradient(to right, var(--accent-500), var(--accent-600))',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(to right, var(--accent-600), var(--accent-700))';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(to right, var(--accent-500), var(--accent-600))';
                    }}
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Missing Course
                  </button>

                  {/* Missing Courses List */}
                  {customCourses.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Your Missing Courses ({customCourses.length})</h3>
                      {customCourses.map((course) => (
                        <div key={course.id} className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-gray-900 dark:text-white">{course.Course}</h4>
                                <span className="px-2 py-0.5 text-xs rounded bg-[var(--accent-100)] text-[var(--accent-700)] dark:bg-[var(--accent-900)] dark:text-[var(--accent-300)]">
                                  {course.course_level}
                                </span>
                              </div>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
                                <span>Grade: {course.Mark}</span>
                                <span>{course.Credits} credits</span>
                                <span>Grade {course.Grade}</span>
                                <span>Term {course.Term}</span>
                                {course.Institution && course.Institution !== 'DVHS' && (
                                  <span className="text-xs">({course.Institution})</span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteCustomCourse(course.id)}
                              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors p-2 -mr-2"
                              aria-label="Delete course"
                            >
                              <svg className="h-6 w-6 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Transcript Management */}
                <div data-section="transcript" className="bg-white dark:bg-gray-900/60 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-gray-800">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Transcript Management</h2>
                  
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                    Upload, manage, and switch between your transcripts. You can also reparse any transcript if there are errors.
                    If courses are missing, you can <button onClick={() => {document.querySelector('[data-section="custom-courses"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });}} className="font-semibold hover:underline" style={{ color: 'var(--accent-600)' }}>add them manually</button>.
                  </p>

                  {/* Transcript Status Indicator */}
                  {transcriptStatus === 'uploading' && (
                    <div className="mb-4 p-4 rounded-lg border bg-white dark:bg-gray-800/50 [border-color:var(--accent-200)] dark:[border-color:color-mix(in_srgb,var(--accent-500)_30%,transparent)]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium [color:var(--accent-700)] dark:[color:var(--accent-400)]">{uploadStage}</span>
                        <span className="text-sm font-bold [color:var(--accent-700)] dark:[color:var(--accent-400)]">{uploadProgress}%</span>
                      </div>
                      <div className="w-full rounded-full h-2 [background-color:var(--accent-100)] dark:bg-gray-700">
                        <div
                          className="h-2 rounded-full transition-all duration-300"
                          style={{ 
                            width: `${uploadProgress}%`,
                            background: `linear-gradient(to right, var(--accent-500), var(--accent-600))`
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {transcriptStatus === 'success' && (
                    <div className="mb-4 p-3 rounded-lg flex items-center gap-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300">
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm font-medium">Transcript uploaded and parsed successfully!</span>
                    </div>
                  )}
                  {transcriptStatus === 'error' && (
                    <div className="mb-4 p-3 rounded-lg flex items-center gap-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="text-sm font-medium">Upload failed. Please try again.</span>
                    </div>
                  )}

                  {/* Upload New Transcript */}
                  <div className="mb-6">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".pdf"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleUploadNewTranscript(e.target.files[0]);
                        }
                      }}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all flex items-center justify-center gap-2"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Upload New Transcript
                    </button>
                  </div>

                  {/* My Transcripts */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">My Transcripts</h3>
                    {loadingTranscripts ? (
                      <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                        <p className="text-gray-600 dark:text-gray-400">Loading transcripts...</p>
                      </div>
                    ) : transcripts.length === 0 ? (
                      <div className="p-8 bg-gray-50 dark:bg-gray-800/60 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-center">
                        <svg className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-200 mb-2">No Transcripts Found</h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          You have no transcripts uploaded. Use the button above to upload your first transcript.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {transcripts.map(transcript => (
                          <div key={transcript.id} className="p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex-1">
                                {editingTranscriptId === transcript.id ? (
                                  <input
                                    type="text"
                                    value={editingTranscriptName}
                                    onChange={(e) => setEditingTranscriptName(e.target.value)}
                                    onBlur={() => handleRenameTranscript(transcript.id, editingTranscriptName)}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleRenameTranscript(transcript.id, editingTranscriptName);
                                      }
                                    }}
                                    className="font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm w-full max-w-xs"
                                    autoFocus
                                  />
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-gray-900 dark:text-white">{transcript.name}</p>
                                    <button
                                      onClick={() => {
                                        setEditingTranscriptId(transcript.id);
                                        setEditingTranscriptName(transcript.name);
                                      }}
                                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    >
                                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                    </button>
                                  </div>
                                )}
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  Uploaded: {new Date(transcript.uploadDate).toLocaleDateString()} at {new Date(transcript.uploadDate).toLocaleTimeString()}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 relative">
                                {transcript.isActive ? (
                                  <span className="px-3 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-semibold rounded-full">
                                    Active
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => handleSetActiveTranscript(transcript.id)}
                                    className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                  >
                                    Set Active
                                  </button>
                                )}
                                <button
                                  onClick={() => setDeleteTranscriptConfirm(transcript.id)}
                                  className="px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                >
                                  Delete
                                </button>
                                {deleteTranscriptConfirm === transcript.id && (
                                  <div className="absolute right-0 top-full mt-1 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 flex gap-2">
                                    <button
                                      onClick={() => handleDeleteTranscript(transcript.id)}
                                      className="px-3 py-1 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                                    >
                                      Delete
                                    </button>
                                    <button
                                      onClick={() => setDeleteTranscriptConfirm(null)}
                                      className="px-3 py-1 text-xs font-semibold bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Parsing Progress Bar */}
                            {transcript.isParsing && (
                              <div className="mt-3">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs text-gray-600 dark:text-gray-400">Parsing transcript...</span>
                                  <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{transcript.parseProgress}%</span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                  <div
                                    className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${transcript.parseProgress}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
      />

      {/* Add Course Modal */}
      {showAddCourseModal && (
        <AddCourseModal
          onClose={() => setShowAddCourseModal(false)}
          onSubmit={handleAddCustomCourse}
          schools={schools}
        />
      )}

      {/* Add School Modal */}
      {showAddSchoolModal && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowAddSchoolModal(false);
            setNewSchool({ name: '', type: 'online-hs' });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowAddSchoolModal(false);
              setNewSchool({ name: '', type: 'online-hs' });
            }
          }}
          tabIndex={-1}
        >
          <div 
            className="bg-white dark:bg-gray-800 border-2 border-[var(--accent-300)] dark:border-[var(--accent-600)] rounded-xl shadow-2xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <div className="text-base font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                <span className="text-[var(--accent-600)] dark:text-[var(--accent-400)]">🏫</span>
                <span>Add School</span>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  School Name
                </label>
                <input
                  type="text"
                  value={newSchool.name}
                  onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newSchool.name.trim()) {
                      handleAddSchool();
                    } else if (e.key === 'Escape') {
                      setShowAddSchoolModal(false);
                      setNewSchool({ name: '', type: 'online-hs' });
                    }
                  }}
                  placeholder="e.g., Crafton Hills College"
                  className="w-full px-3 py-2 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[var(--accent-500)] focus:border-[var(--accent-500)] dark:bg-gray-700 dark:text-white font-medium transition-all"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  School Type
                </label>
                <select
                  value={newSchool.type}
                  onChange={(e) => setNewSchool({ ...newSchool, type: e.target.value })}
                  className="w-full px-3 py-2 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[var(--accent-500)] focus:border-[var(--accent-500)] dark:bg-gray-700 dark:text-white font-medium transition-all"
                >
                  <option value="online-hs">Online High School</option>
                  <option value="community-college">Community College</option>
                </select>
              </div>

              {newSchool.error && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{newSchool.error}</p>
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  onClick={() => {
                    setShowAddSchoolModal(false);
                    setNewSchool({ name: '', type: 'online-hs' });
                  }}
                  disabled={newSchool.loading}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSchool}
                  disabled={!newSchool.name.trim() || newSchool.loading}
                  className={`px-4 py-2 text-white text-sm font-bold rounded-lg transition-all shadow-md hover:shadow-lg disabled:shadow-none disabled:cursor-not-allowed ${
                    newSchool.error 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-[var(--accent-600)] hover:bg-[var(--accent-700)] disabled:bg-gray-400'
                  }`}
                >
                  {newSchool.loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Adding...
                    </span>
                  ) : newSchool.error ? (
                    '✗ Failed'
                  ) : (
                    '✓ Add School'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
